import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Github, ImagePlus, Loader2, LogOut, Save } from "lucide-react";
import { Wordmark } from "@/components/site/Layout";
import { adminApi } from "@/pages/Admin";

const PAGES = [
  { key: "home", label: "Accueil" },
  { key: "tarifs", label: "Tarifs" },
  { key: "apropos", label: "À propos" },
  { key: "contact", label: "Contact" },
];

const setDeep = (obj, path, value) => {
  const key = path[0];
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  copy[key] = path.length === 1 ? value : setDeep(obj[key], path.slice(1), value);
  return copy;
};

/* Éditeur récursif : seules les chaînes de texte sont éditables (structure figée côté API). */
function Fields({ value, path, onChange }) {
  if (typeof value === "string") {
    const long = value.length > 60 || value.includes("\n");
    return (
      <label className="block">
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ostelo-ink/40">{path.join(" › ")}</span>
        {long ? (
          <textarea
            rows={Math.min(6, Math.max(2, value.split("\n").length + 1))}
            value={value}
            onChange={(e) => onChange(path, e.target.value)}
            className="w-full rounded-lg border border-ostelo-deep/15 bg-white px-3.5 py-2.5 text-sm leading-relaxed outline-none focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15"
            data-testid={`field-${path.join("-")}`}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(path, e.target.value)}
            className="w-full rounded-lg border border-ostelo-deep/15 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15"
            data-testid={`field-${path.join("-")}`}
          />
        )}
      </label>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div>
        <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-ostelo-blue">{path.join(" › ")}</span>
        <div className="space-y-3">
          {value.map((item, i) => (
            <div key={i} className="space-y-3 rounded-xl border border-ostelo-deep/10 bg-white p-4">
              <Fields value={item} path={[...path, String(i)]} onChange={onChange} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <div className="space-y-5">
        {Object.entries(value).map(([k, v]) => (
          <Fields key={k} value={v} path={[...path, k]} onChange={onChange} />
        ))}
      </div>
    );
  }
  return null;
}

export default function AdminDashboard({ onLogout }) {
  const [content, setContent] = useState(null);
  const [images, setImages] = useState([]);
  const [github, setGithub] = useState(false);
  const [page, setPage] = useState("home");
  const [lang, setLang] = useState("fr");
  const [tab, setTab] = useState("texts");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    /* En preview, le serveur de dev recharge la page quand une image de public/assets change :
       ce drapeau permet d'afficher la confirmation après le rechargement. */
    if (sessionStorage.getItem("admin_upload_done")) {
      sessionStorage.removeItem("admin_upload_done");
      toast.success("Image remplacée avec succès.");
    }
    Promise.all([adminApi().get("/content"), adminApi().get("/images")])
      .then(([c, i]) => {
        setContent(c.data.content);
        setGithub(c.data.github);
        setImages(i.data.images);
      })
      .catch(() => onLogout());
  }, [onLogout]);

  const onChange = (path, value) => setContent((c) => (c ? { ...c, [lang]: setDeep(c[lang], [page, ...path], value) } : c));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi().put("/content", {
        content: { fr: { [page]: content.fr[page] }, en: { [page]: content.en[page] } },
      });
      if (data.github_pushed) {
        toast.success("Textes enregistrés et poussés sur GitHub — rebuild Netlify en cours (quelques minutes).");
      } else {
        toast.success("Textes enregistrés. Push GitHub inactif : token non configuré.");
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (key, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(key);
    sessionStorage.setItem("admin_upload_done", "1");
    try {
      const { data } = await adminApi().post(`/images/${key}`, fd);
      sessionStorage.removeItem("admin_upload_done");
      toast.success(
        data.github_pushed
          ? "Image remplacée et poussée sur GitHub — rebuild Netlify en cours (quelques minutes)."
          : "Image remplacée. Push GitHub inactif : token non configuré."
      );
      setImages((imgs) => imgs.map((im) => (im.key === key ? { ...im, url: `${im.url.split("?")[0]}?v=${Date.now()}` } : im)));
    } catch (e) {
      sessionStorage.removeItem("admin_upload_done");
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec du remplacement.");
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="min-h-screen bg-ostelo-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-ostelo-deep/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Wordmark light />
            <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white/70">
              Back-office
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span
              className={`hidden items-center gap-1.5 text-xs font-medium sm:flex ${github ? "text-ostelo-green" : "text-white/50"}`}
              data-testid="admin-github-status"
            >
              <Github size={14} />
              {github ? "GitHub connecté — push auto" : "GitHub non configuré (push inactif)"}
            </span>
            <button
              type="button"
              onClick={onLogout}
              className="flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white/80 transition-colors hover:border-ostelo-orange hover:text-ostelo-orange"
              data-testid="admin-logout"
            >
              <LogOut size={13} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-tabs">
          {PAGES.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setPage(p.key);
                setTab("texts");
              }}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                tab === "texts" && page === p.key ? "bg-ostelo-deep text-white" : "bg-white text-ostelo-ink/60 hover:text-ostelo-deep"
              }`}
              data-testid={`admin-tab-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <span className="mx-2 h-5 w-px bg-ostelo-deep/10" aria-hidden="true" />
          <button
            type="button"
            onClick={() => setTab("images")}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
              tab === "images" ? "bg-ostelo-deep text-white" : "bg-white text-ostelo-ink/60 hover:text-ostelo-deep"
            }`}
            data-testid="admin-tab-images"
          >
            Images
          </button>
        </div>

        {tab === "texts" && content && (
          <>
            <div className="mt-6 flex items-center gap-2" data-testid="admin-lang-tabs">
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  className={`rounded-lg px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    lang === l ? "bg-ostelo-blue text-white" : "bg-white text-ostelo-ink/50 hover:text-ostelo-deep"
                  }`}
                  data-testid={`admin-lang-${l}`}
                >
                  {l === "fr" ? "Français" : "English"}
                </button>
              ))}
            </div>
            <div className="mt-5 rounded-2xl border border-ostelo-deep/10 bg-ostelo-ivory/50 p-6" data-testid="admin-editor">
              <Fields value={content[lang][page]} path={[]} onChange={onChange} />
            </div>
            <div className="sticky bottom-4 mt-6 flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-ostelo-orange px-6 py-3.5 text-sm font-bold text-ostelo-deep shadow-[0_10px_30px_rgb(249,157,47,0.35)] transition-transform duration-300 hover:scale-[0.98] disabled:opacity-60"
                data-testid="admin-save-button"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Enregistrer {github ? "et pousser sur GitHub" : ""}
              </button>
            </div>
          </>
        )}

        {tab === "images" && (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-images">
            {images.map((img) => (
              <div key={img.key} className="overflow-hidden rounded-2xl border border-ostelo-deep/10 bg-white" data-testid={`admin-image-${img.key}`}>
                <div className="aspect-[16/10] bg-ostelo-sky">
                  <img src={img.url} alt={img.label} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-4">
                  <p className="text-sm font-bold text-ostelo-deep">{img.label}</p>
                  <p className="mt-0.5 text-xs text-ostelo-ink/50">
                    {img.page} — WebP généré en {img.widths.length} tailles
                  </p>
                  <label
                    className={`mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-ostelo-blue/30 bg-ostelo-sky px-4 py-2.5 text-xs font-bold text-ostelo-blue transition-colors hover:bg-ostelo-blue hover:text-white ${
                      uploading === img.key ? "pointer-events-none opacity-60" : ""
                    }`}
                    data-testid={`admin-upload-${img.key}`}
                  >
                    {uploading === img.key ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                    Remplacer cette image
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => upload(img.key, e.target.files[0])}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
